import React, { FC, useEffect, useMemo } from 'react';
import { setAutomationElement } from '@experian-uk/corvetteuk-common-ui';
import Button from '@nebula/button';
import { BodyLarge400, TitleXSmall500 } from '@nebula/global-styles';
import { ExternalLinkIcon } from '@nebula/icons/ui';
import {
  CancellationType,
  getDeactivateAccountInfo,
  Product,
  PlanType,
  DEACTIVATE_ACCOUNT_TITLE,
  DEFERRED_DEACTIVATE_ACCOUNT_TITLE,
  IMMEDIATE_DEACTIVATE_ACCOUNT_TITLE,
} from '../../../../constants/deactivate';
import products from '../../../../constants/products';
import benefitsDocument from '../../../../constants/benefitsDocument';
import { ButtonContainer, Container, TextContainer } from './index.styles';
import { List, BenefitsLink, BenefitsLinkText } from '../shared.styles';
import { StyledTitleLarge500, StyledBodyLarge400 } from '../ChoiceCancelStage/index.styles';
import { setViewMeta } from '@ecs-uk/web-common-mobile-view-helpers';
import usePageView from '../../../../hooks/usePageView';

interface ConfirmCancelStageProps {
  product: Product;
  planType: PlanType;
  cancellationType: CancellationType;
  formattedNextBillingDate: string;
  isCoolingOffEligible: boolean;
  onConfirm: () => void;
  isWebview?: boolean;
  subscriptionFamily?: string;
  productDisplayName?: string;
  onMySubscriptionClick?: () => void;
}

const getTitle = (cancellationType: CancellationType, formattedDate: string): string => {
  if (cancellationType === null) return DEACTIVATE_ACCOUNT_TITLE;
  if (cancellationType === CancellationType.Deferred) return DEFERRED_DEACTIVATE_ACCOUNT_TITLE(formattedDate);
  return IMMEDIATE_DEACTIVATE_ACCOUNT_TITLE;
};

const ConfirmCancelStage: FC<ConfirmCancelStageProps> = ({
  product,
  planType,
  cancellationType,
  formattedNextBillingDate,
  isCoolingOffEligible,
  onConfirm,
  isWebview = false,
  subscriptionFamily,
  productDisplayName,
  onMySubscriptionClick,
}) => {
  const PAGE_NAME = 'ProdMoveConfirmDeactivationPage';

  usePageView(PAGE_NAME);

  const title = getTitle(cancellationType, formattedNextBillingDate);

  const isPaidNonCoolingOff = planType === PlanType.Paid && !isCoolingOffEligible;
  const isCreditExpert = subscriptionFamily === products.credit_expert;
  const isIdentityPlus = subscriptionFamily === products.identity;
  const showBenefitsLink = isPaidNonCoolingOff && (isCreditExpert || isIdentityPlus);

  const CE_BENEFIT_LINK = `${benefitsDocument.baseUrl}/${
    benefitsDocument[products.credit_expert as keyof typeof benefitsDocument]
  }`;
  const ID_BENEFIT_LINK = `${benefitsDocument.baseUrl}/${
    benefitsDocument[products.identity as keyof typeof benefitsDocument]
  }`;

  const deactivationInfo = useMemo(
    () =>
      getDeactivateAccountInfo({
        product,
        planType,
        cancellationType,
        formattedBillingEndDate: formattedNextBillingDate,
        isCoolingOffEligible,
      }) || { title: '', helperTextList: [] as string[] },
    [product, planType, cancellationType, formattedNextBillingDate, isCoolingOffEligible]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    setViewMeta({ viewTitle: 'Confirm deactivation' });
  }, []);

  return (
    <Container $isWebview={isWebview}>
      <TextContainer>
        <StyledTitleLarge500 as="h1">{title}</StyledTitleLarge500>
        {isPaidNonCoolingOff && onMySubscriptionClick && (
          <StyledBodyLarge400>
            Go to{' '}
            <BenefitsLinkText role="button" tabIndex={0} onClick={onMySubscriptionClick}>
              My subscription
            </BenefitsLinkText>{' '}
            to cancel subscription and keep your account.
          </StyledBodyLarge400>
        )}
        <TitleXSmall500 as="h2">{deactivationInfo.title.trim()}</TitleXSmall500>
        <List $lastItemBottomMargin={!showBenefitsLink ? '32px' : undefined}>
          {deactivationInfo.helperTextList.map((text, index) => (
            <li key={`${text}-${index}`}>
              <BodyLarge400>{text.trim()}</BodyLarge400>
            </li>
          ))}
        </List>
        {showBenefitsLink && (
          <BenefitsLink
            {...setAutomationElement('deactivate-benefits-link')}
            href={isCreditExpert ? CE_BENEFIT_LINK : ID_BENEFIT_LINK}
            target="_blank"
            rel="noreferrer"
          >
            See all the benefits of {productDisplayName}
            <ExternalLinkIcon iconColor="#255AF0" />
          </BenefitsLink>
        )}
      </TextContainer>
      <ButtonContainer>
        <Button
          isButton
          buttonKind="primaryMagenta"
          htmlType="button"
          type="secondary"
          label="Confirm deactivation"
          onClick={onConfirm}
          {...setAutomationElement('confirm-deactivation')}
        />
      </ButtonContainer>
    </Container>
  );
};

export default ConfirmCancelStage;
